// ПЛАН ПРОГРЕВА — ОДИН ФАЙЛ НА ВСЕ ПРИЛОЖЕНИЯ: САЙТ, КЛИЕНТСКОЕ iOS, АДМИНСКОЕ iOS (ТЗ ~/projects/HELP-WARM-FIX-TZ.md §2,
// владелец 26.10.2026: «нигде не перечислена вся логика, в каком порядке прогрев; настраиваешь одно — ломается другое»,
// «план должен лежать файлом, который подходит для всех приложений — веб, iOS»).
//
// ЧТО ЗДЕСЬ ЛЕЖИТ (истина — этот файл; HOW-IT-WORKS §«Данные на фронте и в приложении» на него ссылается):
//   0. Общее для всех: числа очереди (`WARM_QUEUE`), уровни (`WARM_LEVELS`), экраны денег и статусов (`SERVER_TRUTH`).
//   1. Сайт: цели (`SITE_WARM_TARGETS` — что греть и на каком уровне), страницы (`SITE_WARM_PAGES` — КАЖДАЯ страница
//      `app/**/page.tsx`, что на ней греется и в каком порядке), общий вход в кабинет (`SITE_WARM_CABINET`), набор сцен.
//   2. Клиентское iOS: общие цели (`APP_WARM_COMMON`) и правила каждой страницы сервера (`WARM_PLAN`, таблица §4
//      ТЗ APP-WARM — ниже, как была).
//   3. Админское iOS (Scriptera A): свой раздел (`ADM_WARM_PLAN`).
// Приложения получают план ручкой `GET /api/app/warm-plan` (`warmPlanPayload` ниже; только добавления — старые сборки
// её не зовут и не ломаются). Сайт читает файл напрямую.
//
// ИСПОЛНИТЕЛИ — БЕЗ СВОЕЙ ЛОГИКИ «ЧТО И КОГДА»: сайт — `lib/warm.ts` (очередь: `warmTarget`, `warmTargetRun`) и
// `components/cabinet/warm-cache.tsx` (цели входа и страниц по этому плану); страницы сами не греют — они зовут
// `warmTarget('<id цели>', ключ, адрес)`, где id — из `SITE_WARM_TARGETS`, а уровень и порядок берёт исполнитель отсюда.
// Клиентское iOS — `AppWarm`; админское — `AdmWarm` (их перевод на план — следующей частью ТЗ, нужна сборка).
//
// INVARIANT(scriptera): что и когда греть — ТОЛЬКО этот файл. Вне исполнителей прогрев не зовётся (замок
// warm-plan.site.test.ts: `prefetchPersistent`/`warmPersistent`/`warm(`… вне исполнителей роняют тест); каждая страница
// сайта и сервера приложения есть в плане; каждая цель плана используется. Причина: владелец 26.10.2026 — «бардак,
// по всей видимости это в 50 местах». Канон: ТЗ HELP-WARM-FIX §2, docs/HOW-IT-WORKS.md §«Данные на фронте и в приложении».

// ═══════════════════════════════════════════ 0. ОБЩЕЕ ДЛЯ ВСЕХ ПРИЛОЖЕНИЙ ═══════════════════════════════════════════

/** Версия формата плана (растёт при добавлении полей; приложения читают известные им поля, лишние пропускают). */
export const WARM_PLAN_FORMAT = 2;

/**
 * ЧИСЛА ОЧЕРЕДИ САЙТА (PREFETCH-CAREFUL-TZ §2, владелец 11.10.2026: «аккуратный прогрев, чатов миллионы»; числа — его
 * решение, не менять). Числа приложений — `WARM_QUEUES` ниже (у клиентского iOS своё решение владельца 22.10).
 */
export const WARM_QUEUE = {
  /** Фоновых запросов прогрева одновременно (§2 п.3). */
  parallel: 1,
  /** Шаг между запусками, мс (§2 п.3). */
  stepMs: 300,
  /** Касание (палец на строке, наведение) — первым и мимо всех ворот (§2 п.2). */
  touchFirst: true,
  /** Медленная сеть (`saveData`, `slow-2g`/`2g`) — фонового прогрева нет, касания идут (§2 п.4). */
  slowNetwork: false,
  /** Запрос открытого экрана держит фон не дольше, мс (предохранитель §2 п.2). */
  foregroundMaxMs: 5000,
  /** После касания фон ждёт запрос экрана не дольше, мс (щель «касание → запрос экрана», приёмка 11.10). */
  touchHoldMs: 1000,
  /** Ключ, прогретый или проверенный меньше стольких мс назад, повторно не греется и не проверяется сервером
   *  (= MOUNT_FRESH_MS сайта; это НЕ «показывать или нет» — сохранённое показывается всегда, §1 ТЗ). */
  recheckMs: 60_000,
  /** Ворота «после первого экрана»: `load` + простой браузера (requestIdleCallback), не дольше, мс. */
  idleTimeoutMs: 2000,
  /** Окно «уже грели»: ключ, прогретый моложе, повторно не греется (у сайта = recheckMs). */
  warmedMs: 60_000,
  /** ПЕРЕГРЕВ ПО ВОЗРАСТУ КОПИИ (ТЗ ~/projects/SPEED-TZ.md §5.4, П1 «сохранённое — в первый кадр, свежее — фоном»):
   *  копия СПИСКА (переписки, боты, контакты, подключения, «Уведомления»; `warmFreshMs`) моложе — в очередь фона не
   *  ставится. Касание — как прежде (`warmedMs` + верность данных). */
  listFreshMs: 15 * 60_000,
  /** То же для редко меняющегося (справка, настройки, каталоги) — §5.4. */
  staticFreshMs: 60 * 60_000,
  /** БЮДЖЕТ ФОНА НА ЗАГРУЗКУ СТРАНИЦЫ (§5.4, П5 «остальное — с бюджетом»): полосы 2–3 вместе — не больше стольких
   *  запросов и байт по проводу; кончился — строка журнала `warm-budget`, полосы 2–3 стоят до перезагрузки.
   *  Полоса 1 и касание — без бюджета. */
  budget: { requests: 40, bytes: 1_000_000 },
  /** Строка списка греется, только пробыв в окне прогрева столько мс (§8.2: прокрутка мимо не греет). */
  inViewDwellMs: 300,
} as const;

/** Числа очереди одной платформы (форма одна; значения — свои). */
export interface WarmQueueNumbers {
  parallel: number;
  stepMs: number;
  touchFirst: boolean;
  slowNetwork: boolean;
  foregroundMaxMs: number;
  touchHoldMs: number;
  recheckMs: number;
  idleTimeoutMs: number;
  warmedMs: number;
  /** Только сайт (SPEED §5.4, §8.2) — приложения эти поля не читают. */
  listFreshMs?: number;
  staticFreshMs?: number;
  budget?: { requests: number; bytes: number };
  inViewDwellMs?: number;
}

/**
 * ЧИСЛА ОЧЕРЕДИ ПО ПРИЛОЖЕНИЯМ — один файл, свои значения (решение ведущего 26.10.2026 по итогам iOS-исполнителя):
 *   • `site` — PREFETCH-CAREFUL §2: 1 запрос, шаг 300 мс, окно 60 с;
 *   • `app` (клиентское iOS) — решение владельца 22.10.2026 (~/projects/APP-WARM-TZ.md З-5: «по одному» владелец не
 *     говорил): 4 запроса одновременно, без паузы, окно «уже грели» 10 с (`AppWarm.parallel/step/fresh`);
 *     `recheckMs` (правды сервера) — 60 с, как у сайта;
 *   • `adm` (Scriptera A) — как `app` (своего решения владельца нет); исполнитель — `AdmWarm` (`queues.adm`).
 * Верхнее поле ответа `queue` = `site` (так его читают сборки, вышедшие 26.10); приложение берёт `queues.app`.
 */
export const WARM_QUEUES: Readonly<Record<'site' | 'app' | 'adm', WarmQueueNumbers>> = {
  site: WARM_QUEUE,
  app: {
    parallel: 4,
    stepMs: 0,
    touchFirst: true,
    slowNetwork: false,
    foregroundMaxMs: 5000,
    touchHoldMs: 1000,
    recheckMs: 60_000,
    idleTimeoutMs: 2000,
    warmedMs: 10_000,
  },
  adm: {
    parallel: 4,
    stepMs: 0,
    touchFirst: true,
    slowNetwork: false,
    foregroundMaxMs: 5000,
    touchHoldMs: 1000,
    recheckMs: 60_000,
    idleTimeoutMs: 2000,
    warmedMs: 10_000,
  },
};

/** Уровень цели: У1 — на входе сразу; У2 — после первого экрана; У3 — в простой. Одни для всех приложений (§2 п.2). */
export type WarmLevel = 1 | 2 | 3;
export const WARM_LEVELS: Readonly<Record<WarmLevel, string>> = {
  1: 'на входе, сразу после первого экрана: общее и небольшое (корни вкладок, первый ролик пасхалки; на своей странице — её общее: справка с телами статей на «Аккаунте», снимки открытой статьи)',
  2: 'после первого экрана: цели видимых строк (и полэкрана вперёд), их картинки, соседние папки',
  3: 'в простой: остальное по списку страницы (подстраницы настроек, картинки статей ниже первого экрана, остальные ролики)',
};
/** Касание — над всеми уровнями (полоса 0 очереди): человек уже открывает экран. */
export const WARM_TOUCH = 'касание: первым, мимо всех ворот (первый экран, шаг, медленная сеть, пауза)';

/**
 * ЭКРАНЫ ДЕНЕГ И СТАТУСОВ — ПОКАЗ ТОЛЬКО ПО ОТВЕТУ СЕРВЕРА (решение ведущего 26.10.2026, ТЗ HELP-WARM-FIX §1): баланс
 * токенов, тариф/подписка, оплата, тумблер «бот включён». Остальное сохранённое идёт в первый кадр всегда; эти — как
 * до ТЗ: сохранённое старше `WARM_QUEUE.recheckMs` в первый кадр не идёт, стоит заглушка формы до ответа сервера.
 * `mode: 'screen'` — так ведёт себя весь ключ (хук `usePersistentData` сам смотрит в этот список по ключу);
 * `mode: 'field'` — ключ показывается как все, а ждёт подтверждения сервера только названный элемент
 * (`confirmed` хука; на телефоне — тот же элемент страницы). `app` — та же правда в приложении.
 */
export interface ServerTruthEntry {
  /** Ключ кэша сайта; `prefix: true` — все ключи с этой приставкой. */
  key: string;
  prefix?: boolean;
  mode: 'screen' | 'field';
  what: string;
  /** Страницы сервера приложения (ключ реестра `app-pages`), где та же правда. */
  app?: readonly string[];
}
export const SERVER_TRUTH: readonly ServerTruthEntry[] = [
  { key: 'cabinet:billing', prefix: true, mode: 'screen', what: 'тариф и подписка, котировки докупки, платёжные данные', app: ['account/plan'] },
  { key: 'cabinet:overview', mode: 'screen', what: 'баланс токенов («Баланс AI», отчёт трат)' },
  { key: 'cabinet:overview:spend', mode: 'screen', what: 'траты токенов «Обзора»' },
  { key: 'cabinet:spend-report:', prefix: true, mode: 'screen', what: 'отчёт трат токенов за период' },
  { key: 'cabinet:scenarios', mode: 'field', what: 'тумблер «бот включён» окна бота (st / chainActive)', app: ['^bot\\/([^/]+)$'] },
];

/** Ключ — из экранов денег и статусов с показом целиком (`mode: 'screen'`)? */
export function isServerTruthKey(key: string): boolean {
  return SERVER_TRUTH.some((e) => e.mode === 'screen' && (e.prefix ? key.startsWith(e.key) : key === e.key));
}

// ═══════════════════════════════════════════════════ 1. САЙТ ═══════════════════════════════════════════════════════

export interface SiteWarmTarget {
  level: WarmLevel;
  /** Что греется — словами (для отчёта и HOW-IT-WORKS). */
  what: string;
  /** Где исполняется, если не очередь `warm.ts` (тяжёлое со своим жизненным циклом: скрытые кадры, монтаж разделов). */
  runner?: 'tab-stack' | 'warm-demo-scenes';
  /** Полоса 1, но ПОСЛЕ запросов открытого экрана (`beginForeground`) и до полосы 2 (SPEED §6.4: ролик). Прочие цели
   *  У1 запросы экрана не ждут. */
  afterScreen?: true;
}

/** Сколько первых снимков каждой статьи справки — уровень 1 (первый экран статьи); остальные — уровень 3. */
export const HELP_FIRST_IMAGES = 2;

/**
 * ЦЕЛИ САЙТА. Уровень — здесь; страница/список только называет цель (`warmTarget('<id>', ключ, адрес)`), касание —
 * `urgent` (над всеми уровнями). Порядок внутри уровня — порядок постановки (у входа и страниц — порядок списка ниже).
 */
export const SITE_WARM_TARGETS = {
  // ── У1: вход в кабинет (SPEED §5.1: только корни вкладок и первый ролик) и вход в раздел ──
  'help.index': { level: 1, what: 'оглавление справки с телами всех статей (GET /api/help?locale=), хранится с версией сайта — на «Аккаунте» (там справка)' },
  'help.images.first': { level: 1, what: `первые ${HELP_FIRST_IMAGES} снимка ОТКРЫТОЙ статьи справки (зовёт окно статьи, help-article.tsx; SPEED §6.3)` },
  niche: { level: 2, what: 'ниша человека (GET /api/user/niche?locale=) — по ней фото публикации в сцене мастеров; на «Ботах» (SPEED §5.3)' },
  'scene.photos': { level: 2, what: 'фото телефона-сцены (общие файлы сцены, аватарки и публикация по нише человека — scene-assets.ts), без картинок лендинга; на «Ботах» после ниши (SPEED §5.3а)' },
  stories: { level: 1, what: 'полоса пасхалки «Ботов» — кружки (GET /api/cabinet/help-stories?lang=); экран «Ботов» читает сам, первый ролик ждёт её тем же ключом' },
  'stories.video.first': { level: 1, afterScreen: true, what: 'первый ролик пасхалки (на него ведёт экран после регистрации): сразу после корней вкладок, после запросов открытого экрана и до полосы 2 (SPEED §6.4); лёгкая версия mid, на 3g — low; оригинал заранее никогда, на медленной сети — нет' },
  'tabs.panes': { level: 1, what: 'корни вкладок: разделы верхнего уровня монтируются со своими данными после первого экрана', runner: 'tab-stack' },
  // ── У2: видимое на открытом экране ──
  'bots.row': { level: 2, what: 'окно бота / черновик мастера видимых карточек «Ботов» и полэкрана вперёд' },
  'bots.pulse': { level: 2, what: 'пульс запусков — линия открытий на карточках «Ботов» (только приложение)' },
  'bots.scenes.edit': { level: 2, what: 'сцены правки ботов списка, потолок набора — только натив старых сборок (мост wizWarm); в браузере — ничего', runner: 'warm-demo-scenes' },
  'bots.scenes.create': { level: 2, what: 'сцены каталога «Из шаблона» заранее со страницы «Ботов» — только натив старых сборок (мост wizWarm); в браузере — ничего', runner: 'warm-demo-scenes' },
  'templates.scenes': { level: 2, what: 'сцены каталога «Из шаблона», пока он открыт — только натив старых сборок (мост wizWarm); в браузере — ничего', runner: 'warm-demo-scenes' },
  'templates.titles': { level: 2, what: 'названия ботов (номер «<шаблон> N» в поле «Название» мастера) — до открытия каталога/мастера' },
  'bot.events': { level: 2, what: 'ленты событий окна бота («Контакты» и «Все») за сохранённый период' },
  'bot.chain-stats': { level: 2, what: 'цифры цепочки в окне цепочки' },
  'bot.broadcast': { level: 2, what: 'состояние рассылки в её окне' },
  'bot.journal.contacts': { level: 2, what: 'карточки первых людей журнала событий окна бота' },
  'bot.runs': { level: 2, what: 'первая страница журнала запусков и график окна бота' },
  'bot.runs.details': { level: 2, what: 'детали первых прогонов журнала запусков' },
  'chats.feed': { level: 2, what: 'лента видимых переписок (лёгкая, 20 сообщений; касание — полная, находка — страница вокруг)' },
  'chats.folders': { level: 2, what: 'первая страница соседних папок «Чатов» (свайп по папкам; в браузере — после первого касания раздела, WEB-CAUSES М42 п.3)' },
  'contacts.card': { level: 2, what: 'история видимых людей «Контактов»; касание — ещё прогноз цепочек, переписка, метки' },
  'contacts.folders': { level: 2, what: 'первые страницы соседних папок «Контактов»' },
  'contacts.list-ahead': { level: 3, what: 'список «Контактов» и страницы его папок заранее с «Аккаунта» (только приложение)' },
  'connections.cards': { level: 2, what: 'карточки подключённых аккаунтов и схемы их форм' },
  'connections.schemas': { level: 3, what: 'схемы форм всех сервисов каталога подключений' },
  'connections.stats': { level: 2, what: 'свод аккаунтов статистики и статистика каждого за 30 дней' },
  'account.billing-summary': { level: 3, what: '«Подписка» — сводка (на «Аккаунте» — У2)' },
  'account.overview': { level: 3, what: '«Баланс AI» — баланс (на «Аккаунте» — У2)' },
  'account.spend-report': { level: 3, what: '«Баланс AI» — отчёт трат за 30 дней (на «Аккаунте» — У2)' },
  'billing.add-quote': { level: 2, what: 'котировка докупки аккаунтов (Pro на странице «Подписка»)' },
  'billing.payer': { level: 2, what: 'платёжные данные слоя «Оплата»' },
  'ai.jobs': { level: 2, what: 'история генераций (только админ и «только модели»)' },
  'ai.overview': { level: 2, what: 'сводка моделей' },
  'ai.family': { level: 2, what: 'карточки моделей каталога' },
  // ── У3: подстраницы «Настроек» и «Аккаунта» — на своих страницах (SPEED §5.3) ──
  'settings.notifications': { level: 3, what: '«Уведомления»' },
  'settings.security': { level: 3, what: '«Безопасность»' },
  'settings.contact-tags': { level: 3, what: 'метки людей' },
  'settings.payer': { level: 3, what: '«Платёжные данные»' },
  'settings.contact-folders': { level: 3, what: '«Папки → Контакты»' },
  'settings.accounts': { level: 3, what: '«Подключения» — список' },
  'settings.accounts-catalog': { level: 3, what: '«Подключения» — каталог сервисов' },
  'settings.tz': { level: 3, what: '«Язык и регион»' },
  'chats.folders-list': { level: 3, what: '«Папки → Чаты»' },
  'chats.accounts': { level: 3, what: 'аккаунты папок «Чатов»' },
  'help.images.rest': { level: 3, what: `первые ${HELP_FIRST_IMAGES} снимка СЛЕДУЮЩЕЙ статьи по оглавлению — одна статья, в простой (SPEED §6.3)` },
  'stories.video.rest': { level: 3, what: 'остальные ролики полосы пасхалки — когда полоса показалась (лёгкие версии, по одному)' },
  // ── Браузерная админка (Scriptera A в браузере) ──
  'adm.lead': { level: 2, what: 'карточка клиента (первые ряды списка; палец — сразу)' },
  'adm.lead-bots': { level: 2, what: '«Боты клиента» открытого профиля' },
  'adm.lead-bot': { level: 2, what: 'страница бота клиента (палец на строке)' },
  'adm.ahead': { level: 2, what: 'шторка «Что уйдёт» строк «Что впереди» открытого профиля' },
  'adm.thread': { level: 2, what: 'лента переписки (видимые — лёгкая, палец/профиль — полная)' },
  'adm.folders': { level: 2, what: 'первые страницы прочих папок списка' },
} as const satisfies Record<string, SiteWarmTarget>;

export type SiteWarmTargetId = keyof typeof SITE_WARM_TARGETS;

/** Цель страницы: id, (не обязательно) свой уровень на этой странице и условие. */
export interface SiteWarmEntry {
  id: SiteWarmTargetId;
  level?: WarmLevel;
  /** `'bot-window'` — только пока открыто окно бота (адрес `/app/bots/<id бота>`); со списка «Ботов» фоном не греется,
   *  касание карточки — как всегда (SPEED §5.3). Исполнитель — `lib/warm.ts` (`warmWhenOk`). */
  when?: 'bot-window';
}
export interface SiteWarmPage {
  /** Цели по порядку. Пусто — на странице нечего греть (`note` — почему). */
  targets: readonly SiteWarmEntry[];
  note?: string;
}

const t = (id: SiteWarmTargetId, level?: WarmLevel, when?: SiteWarmEntry['when']): SiteWarmEntry => ({
  id,
  ...(level ? { level } : {}),
  ...(when ? { when } : {}),
});

/**
 * ВХОД В КАБИНЕТ (`/app/*`, оболочка кабинета — WarmCache): ровно корни вкладок и первый ролик пасхалки (ТЗ
 * ~/projects/SPEED-TZ.md §5.1, §6.4; владелец 27.10.2026: «Ролик очень рано требуется… приоритет хороший»). Всё прочее —
 * на своих страницах (`SITE_WARM_PAGES`, §5.3): справка — на «Аккаунте», ниша и сцена — на «Ботах», подстраницы —
 * у своих разделов; скрытые от клиента разделы не греются вовсе (§5.2).
 */
export const SITE_WARM_CABINET: SiteWarmPage = { targets: [t('tabs.panes'), t('stories.video.first')] };

const none = (note: string): SiteWarmPage => ({ targets: [], note });
const NO_DATA = 'публичная страница: данных кабинета нет, прогревать нечего';
const ADMIN = 'админка в браузере: своих прогревов нет';
const IN_CABINET = 'общий вход в кабинет — SITE_WARM_CABINET; своих целей у страницы нет';

/**
 * КАЖДАЯ СТРАНИЦА САЙТА (`apps/web/src/app/**\/page.tsx`, путь без `page.tsx`) — что на ней греется. Страницы
 * кабинета, кроме того, получают общий вход SITE_WARM_CABINET. Замок warm-plan.site.test.ts: новая страница без
 * записи (и запись без страницы) роняет тест.
 */
export const SITE_WARM_PAGES: Readonly<Record<string, SiteWarmPage>> = {
  'chat/[token]': none(NO_DATA),
  'form/[token]': none(NO_DATA),
  'pay/back/[shop]': none('возврат из кассы: переадресация'),
  'pay/back/[shop]/fail': none('возврат из кассы: переадресация'),
  'pay/back/st/[ref]': none('возврат из Stripe: переадресация'),
  'pay/back/st/[ref]/fail': none('возврат из Stripe: переадресация'),
  'pay/back/yk/[ref]': none('возврат из ЮKassa: переадресация'),
  '[locale]': none('лендинг: ссылки next/link, своих прогревов нет'),
  '[locale]/auth-wait': none(NO_DATA),
  '[locale]/data-deletion': none(NO_DATA),
  '[locale]/help': none('справка собрана заранее; ссылки — next/link (Next сам подгружает видимые ссылки)'),
  '[locale]/help/[section]': none('справка собрана заранее; ссылки — next/link'),
  '[locale]/help/[section]/[slug]': none('справка собрана заранее; ссылки — next/link'),
  '[locale]/login': none(NO_DATA),
  '[locale]/oauth/authorize': none(NO_DATA),
  '[locale]/oferta': none(NO_DATA),
  '[locale]/privacy': none(NO_DATA),
  '[locale]/register': none(NO_DATA),
  '[locale]/sisbaksbsjsjwbskebrbnsb': none(NO_DATA),
  '[locale]/s/[token]': none(NO_DATA),
  '[locale]/studio-render/[id]': none('служебный кадр рендера'),
  '[locale]/transfer/[token]': none(NO_DATA),
  '[locale]/unsubscribed': none(NO_DATA),
  '[locale]/verify-phone': none(NO_DATA),
  '[locale]/welcome': none(NO_DATA),
  '[locale]/wizard-preview': none('кадр сцены мастера (его греют сцены — bots.scenes.*)'),
  '[locale]/wizard-scene/[scene]': none('кадр сцены мастера (его греют сцены — bots.scenes.*)'),
  // ── Кабинет ──
  '[locale]/app': none(IN_CABINET),
  '[locale]/app/bots/[[...rest]]': {
    targets: [
      t('bots.row'),
      t('bots.pulse'),
      t('templates.titles'),
      t('niche'),
      t('scene.photos'),
      t('stories.video.rest'),
      t('bots.scenes.create'),
      t('bots.scenes.edit'),
      t('templates.scenes'),
      // Содержимое окна бота — только при открытом окне (SPEED §5.3): со списка греется лишь черновик (`bots.row`).
      t('bot.events', undefined, 'bot-window'),
      t('bot.runs', undefined, 'bot-window'),
      t('bot.runs.details', undefined, 'bot-window'),
      t('bot.chain-stats', undefined, 'bot-window'),
      t('bot.broadcast', undefined, 'bot-window'),
      t('bot.journal.contacts', undefined, 'bot-window'),
    ],
    note: 'ниша и сцена — после первого экрана (мастер тоже здесь, /app/bots/new); сцены bots.scenes.*/templates.scenes — только натив старых сборок (SPEED §7.3)',
  },
  '[locale]/app/scripts/[[...rest]]': none('старый адрес «Ботов» — то же, что /app/bots'),
  '[locale]/app/messages/[[...rest]]': {
    targets: [t('chats.feed'), t('chats.folders')],
    note: 'ленты — строки, пробывшие в окне ≥ WARM_QUEUE.inViewDwellMs (SPEED §8.2); списки других состояний — по касанию фильтра',
  },
  '[locale]/app/contacts/[[...rest]]': {
    targets: [t('contacts.card'), t('contacts.folders')],
    note: 'карточки — строки, пробывшие в окне ≥ WARM_QUEUE.inViewDwellMs (SPEED §5.3)',
  },
  '[locale]/app/broadcasts/[[...rest]]': { targets: [t('bot.broadcast'), t('bot.runs'), t('bot.runs.details')] },
  '[locale]/app/account/[[...rest]]': {
    targets: [
      t('account.billing-summary', 2),
      t('account.overview', 2),
      t('account.spend-report', 2),
      t('settings.notifications'),
      t('settings.security'),
      t('settings.tz'),
      t('help.index'),
    ],
    note: 'котировка докупки, платёжные данные, список «Контактов» заранее — по касанию «Подписка»/«Оплата» (SPEED §5.3)',
  },
  '[locale]/app/settings/[[...rest]]': {
    targets: [
      t('settings.accounts'),
      t('settings.accounts-catalog'),
      t('settings.contact-tags'),
      t('settings.contact-folders'),
      t('chats.folders-list'),
      t('chats.accounts'),
      t('connections.cards'),
    ],
    note: 'схемы форм и статистика подключений — по касанию карточки подключения (SPEED §5.3)',
  },
  '[locale]/app/settings/earnings-materials': none(IN_CABINET),
  // SPEED §5.3 (приёмка ведущего): схемы всех сервисов каталога и статистика каждого аккаунта — по касанию карточки, не фоном.
  '[locale]/app/connections/[[...rest]]': { targets: [t('connections.cards')] },
  '[locale]/app/analytics/[[...rest]]': none(IN_CABINET),
  '[locale]/app/automations': none(IN_CABINET),
  '[locale]/app/automations/[id]': none(IN_CABINET),
  '[locale]/app/products/[[...rest]]': none(IN_CABINET),
  '[locale]/app/ai': { targets: [t('ai.jobs'), t('ai.overview'), t('ai.family')] },
  '[locale]/app/ai/[slug]': { targets: [t('ai.jobs'), t('ai.overview')] },
  '[locale]/app/ai/audio': { targets: [t('ai.jobs'), t('ai.overview')] },
  '[locale]/app/ai/image': { targets: [t('ai.jobs'), t('ai.overview')] },
  '[locale]/app/ai/music': { targets: [t('ai.jobs'), t('ai.overview')] },
  '[locale]/app/ai/video': { targets: [t('ai.jobs'), t('ai.overview')] },
  '[locale]/app/ai/voice': { targets: [t('ai.jobs'), t('ai.overview')] },
  // ── Браузерная админка Scriptera A ──
  '[locale]/app/(adm)/adm': none(ADMIN),
  '[locale]/app/(adm)/adm/prices': none(ADMIN),
  '[locale]/app/(adm)/chats': { targets: [t('adm.thread'), t('adm.lead'), t('adm.folders')] },
  '[locale]/app/(adm)/chats/guest/[telegramId]': { targets: [t('adm.thread')] },
  '[locale]/app/(adm)/chats/[userId]': { targets: [t('adm.thread'), t('adm.lead')] },
  '[locale]/app/(adm)/feed': none(ADMIN),
  '[locale]/app/(adm)/sales': {
    targets: [t('adm.lead'), t('adm.lead-bots'), t('adm.lead-bot'), t('adm.ahead'), t('adm.thread'), t('adm.folders')],
  },
  '[locale]/app/admin/[[...tab]]': none(ADMIN),
  '[locale]/app/admin/backups': none(ADMIN),
  '[locale]/app/admin/bloggers': none(ADMIN),
  '[locale]/app/admin/icons': none(ADMIN),
  '[locale]/app/admin/integrations': none(ADMIN),
  '[locale]/app/admin/kit': none(ADMIN),
  '[locale]/app/admin/palette': none(ADMIN),
  '[locale]/app/admin/reels': none(ADMIN),
  '[locale]/app/admin/screens': none(ADMIN),
};

/**
 * СЦЕНЫ МАСТЕРА — ТЯЖЁЛОЕ (скрытый кадр/WKWebView на адрес, десятки МБ): свой исполнитель `lib/warm-demo-scenes.ts`.
 * Потолок набора и приоритет заказчиков — здесь (договор с нативом О5.10-Б: числа обязаны совпадать).
 */
export const WARM_SCENE_LIMIT = 8;
export const WARM_SCENE_SOURCES = {
  /** Каталог «Из шаблона» открыт — человек вот-вот выберет шаблон. */
  templates: { priority: 0, target: 'templates.scenes' },
  /** Те же адреса каталога, заказанные со страницы «Боты» заранее (владелец 24.09.2026). */
  templatesAhead: { priority: 1, target: 'bots.scenes.create' },
  /** Список «Боты»: правка по тапу — следом. */
  bots: { priority: 2, target: 'bots.scenes.edit' },
} as const satisfies Record<string, { priority: number; target: SiteWarmTargetId }>;

/** Уровень цели на странице (запись страницы может поднять/опустить уровень цели). */
export function siteLevel(id: SiteWarmTargetId, page?: SiteWarmPage): WarmLevel {
  return page?.targets.find((e) => e.id === id)?.level ?? SITE_WARM_TARGETS[id].level;
}

/**
 * СКОЛЬКО ЖИВЁТ КОПИЯ КЛЮЧА ДЛЯ ФОНА (SPEED §5.4): моложе — фоновый прогрев её не трогает (экран сам сверит при
 * открытии). Списки — `listFreshMs`, прочее (справка, настройки, каталоги) — `staticFreshMs`. Экраны денег и статусов
 * (SERVER_TRUTH, `mode: 'screen'`) — `recheckMs`: их копия старше в первый кадр не идёт, поэтому прогрев не откладывается.
 */
export const WARM_LIST_URLS: readonly RegExp[] = [
  /^\/api\/cabinet\/messages(?:[/?]|$)/,
  /^\/api\/cabinet\/scenarios(?:[/?]|$)/,
  /^\/api\/cabinet\/contacts(?:[/?]|$)/,
  /^\/api\/cabinet\/accounts(?!\/catalog)(?:[/?]|$)/,
  /^\/api\/user\/notify-inbox(?:[/?]|$)/,
];
export function warmFreshMs(key: string, url: string): number {
  if (isServerTruthKey(key)) return WARM_QUEUE.recheckMs;
  return WARM_LIST_URLS.some((re) => re.test(url)) ? WARM_QUEUE.listFreshMs : WARM_QUEUE.staticFreshMs;
}

/** Страница плана, чьи записи с `when` проверяет очередь (`lib/warm.ts` `warmWhenOk`). */
export const SITE_BOTS_PAGE = '[locale]/app/bots/[[...rest]]';
/** Окно бота открыто: адрес `/app/bots/<uuid черновика>` (в т.ч. `/stats`, с языком впереди). */
export const BOT_WINDOW_PATH = /\/app\/bots\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:[/?#]|$)/i;

// ═══════════════════════════════════════════ 2. КЛИЕНТСКОЕ iOS ═══════════════════════════════════════════════════════

/**
 * ОБЩИЕ ЦЕЛИ ПРИЛОЖЕНИЯ (не страница реестра): что исполнитель `AppWarm` греет сам и в каком порядке. `replaces` —
 * какая своя логика приложения переезжает сюда (после перевода она перестаёт решать «что и когда»). `ref` — адрес или
 * ключ: `{lang}` — язык кабинета, `{section}`/`{slug}` — из оглавления справки.
 */
export interface AppWarmCommon {
  id: string;
  level: WarmLevel;
  kind: 'pages' | 'page' | 'island' | 'json' | 'story-video' | 'image';
  ref: string;
  what: string;
  replaces?: string;
  /** ЦЕЛЬ СТАРЫХ СБОРОК (ТЗ ~/projects/NATIVE-SCENES-TZ.md §3.2 п.5): сборка, которая умеет цель `replacedBy`, эту цель
   *  не исполняет — её работу делает та. Старые сборки поле не читают и исполняют цель как раньше. */
  replacedBy?: string;
}
export const APP_WARM_COMMON: readonly AppWarmCommon[] = [
  { id: 'launch', level: 1, kind: 'pages', ref: 'GET /api/app/pages/launch?locale={lang}', what: 'пачка страниц запуска — правила `launch` WARM_PLAN (три вкладки и всё, что на них нажимается)', replaces: 'LaunchTail (кроме пачки)' },
  { id: 'help.articles', level: 1, kind: 'island', ref: '/{lang}/help/{section}/{slug}?app=1&bare=1 (+ n.*, p.* — ниша и лицо: адрес берётся из страницы help/<раздел>/<статья>, `url` ряда)', what: 'островки статей справки: пул прогретых; взятая статья после закрытия возвращается в пул (§3)', replaces: 'PageController.warmHelpIslands, WebIsland.warm' },
  { id: 'niche', level: 1, kind: 'json', ref: 'GET /api/user/niche?locale={lang}', what: 'ниша человека — по ней фото сцены' },
  { id: 'scene.photos', level: 1, kind: 'image', ref: 'ответ niche: profile.avatar, profile.postImg, profile.faces, accounts[].avatarUrl/postImg/faces; общие файлы сцены', what: 'фото телефона-сцены статей и мастеров (§5)' },
  { id: 'stories', level: 1, kind: 'json', ref: 'GET /api/help/stories?lang={lang}', what: 'полоса пасхалки — плитки и их статьи (статьи — островки help.articles по `page`)', replaces: 'StoryPageWarm (что и когда)' },
  { id: 'stories.pages', level: 1, kind: 'island', ref: 'items[].page + ?app=1&bare=1', what: 'статьи плиток пасхалки — до открытия полосы; закрытая возвращается в запас (§3)', replaces: 'StoryPageWarm.take без возврата' },
  { id: 'stories.video.first', level: 1, kind: 'story-video', ref: 'первый items[] с video', what: 'первый ролик пасхалки (экран после регистрации): лёгкая версия (videoMid, на медленной — videoLow), остальное — при просмотре', replaces: 'StoryDownloadQueue (порядок)' },
  // Сцены-примеры нативно (ТЗ NATIVE-SCENES §3.2 п.5): пакет сценариев всех сцен — один JSON; сцены своих ботов приезжают
  // внутри страниц мастера (`demo.script`), картинки человека — целью `scene.photos`. Островки сцен (`wizard.scenes`) —
  // только старым сборкам.
  { id: 'scenes.pack', level: 1, kind: 'json', ref: 'GET /api/app/scenes?locale={lang}', what: 'пакет сцен-примеров: свежий — на диск, показ — со следующего открытия' },
  { id: 'wizard.scenes', level: 2, kind: 'page', ref: 'bots/scenes (ответ: scenes.ahead — сцены каталога «Из шаблона», scenes.bots — сцены правки своих ботов; потолок WARM_SCENE_LIMIT)', what: 'сцены мастера (веб-вью сцены) — после первого экрана «Ботов», как сейчас по факту', replaces: 'LaunchTail (сцены мастера), WebIsland.orderScenes', replacedBy: 'scenes.pack' },
  { id: 'stories.video.rest', level: 3, kind: 'story-video', ref: 'остальные items[] с video', what: 'остальные ролики полосы — по одному в простой', replaces: 'StoryDownloadQueue (порядок)' },
];

/** Правило страницы приложения → уровень: пачка запуска — У1, одно касание показанного экрана — У2, очередь — У3. */
export function appRuleLevel(when: WarmWhen): WarmLevel | null {
  if (when === 'launch') return 1;
  if (when === 'touch') return 2;
  if (when === 'queue') return 3;
  return null;
}

// ── 2б. ПРАВИЛА КАЖДОЙ СТРАНИЦЫ СЕРВЕРА ПРИЛОЖЕНИЯ ──
//
// ПРАВИЛО ПРОГРЕВА КАЖДОЙ СТРАНИЦЫ ПРИЛОЖЕНИЯ (ТЗ ~/projects/APP-WARM-TZ.md ред. 5, З-1; таблица §4).
//
// На КАЖДЫЙ ключ реестра `pageBuilders` и КАЖДЫЙ шаблон `pagePatterns` (lib/app-pages/index.ts) — список правил
// `{ when, from, row }` ровно по строкам таблицы §4 ТЗ: П → `launch` (пачка при запуске, `GET /api/app/pages/launch`,
// З-2), О → `touch` (одно касание показанного экрана — родителя `from`, З-4), Ч → `queue` (очередь после О, З-6 п.4),
// служебные ключи — `never`. Ключ записи — сам ключ реестра или `re.source` шаблона. Один ключ реестра может нести
// несколько строк §4 (например, `thread` — переписка из «Чатов», из окна бота, из карточки, ранние сообщения).
//
// У правил `launch` есть `pick` — как пачка находит ключи этой строки для человека (lib/app-pages/launch.ts): ровно
// тот `open`, что приложение получило бы с показанной страницы (`fixed` — ключ как есть; `folders` — пилюли полосы
// папок корня; `rows` — строки первой страницы списка корня, `add` — query, которым приложение греет страницу).
//
// INVARIANT(scriptera): у каждой страницы сервера есть правило прогрева — новая страница без записи здесь роняет
// warm-plan.test.ts (и запись без страницы — тоже). Правила — ровно по таблице §4 ТЗ, номер строки обязателен.
// Причина: владелец 22.10.2026 «ни одной страницы без прогрева» (Р4), «в ТЗ должны быть описаны все страницы».
// Канон: ~/projects/APP-WARM-TZ.md §4, З-1.

/** Когда греть: П → `launch`, О → `touch`, Ч → `queue`, служебные → `never` (ТЗ §4, обозначения). */
export type WarmWhen = 'launch' | 'touch' | 'queue' | 'never';

/** Как пачка запуска находит ключи строки (только у `launch`). */
export type WarmPick =
  /** Ключ как есть (`open` кнопки или строки показанного корня). */
  | { kind: 'fixed'; key: string }
  /** Пилюли полосы папок корня: `<root>?folder=<id>` каждой; «все» (`all`) — сам корень. */
  | { kind: 'folders'; root: 'bots' | 'chats' }
  /** `open` строк первой страницы списка корня, подходящие под `re`; `add` — query прогрева (лёгкая лента `limit=20`). */
  | { kind: 'rows'; root: 'bots' | 'chats'; re: RegExp; add?: string };

export interface WarmRule {
  when: WarmWhen;
  /** Родитель: на каком показанном экране это одно касание (или «запуск», «вкладка»). */
  from: string;
  /** Номер строки таблицы §4 ТЗ (`'служ'` — служебный ключ из конца §4). */
  row: WarmRow;
  pick?: WarmPick;
}

/** Все строки таблицы §4 ТЗ: 1–45, 46a–46e, 47–80 (строки «46» без буквы в таблице нет). */
export const WARM_ROWS = [
  ...Array.from({ length: 45 }, (_, i) => String(i + 1)),
  '46a',
  '46b',
  '46c',
  '46d',
  '46e',
  ...Array.from({ length: 34 }, (_, i) => String(i + 47)),
] as const;
export type WarmRow = string;

/** Строки §4.1 — пачка при запуске (1–20). */
export const LAUNCH_ROWS: readonly string[] = WARM_ROWS.slice(0, 20);

const launch = (row: WarmRow, from: string, pick: WarmPick): WarmRule => ({ when: 'launch', from, row, pick });
const touch = (row: WarmRow, from: string): WarmRule => ({ when: 'touch', from, row });
const queue = (row: WarmRow, from: string): WarmRule => ({ when: 'queue', from, row });
const never = (from: string): WarmRule => ({ when: 'never', from, row: 'служ' });

/** Строки карточек «Ботов», ведущие в окно бота (`bot/<id>`, без хвоста) и в мастер черновика (`wizard/<id>`, не `new`). */
const BOT_WINDOW_OPEN = /^bot\/[^/?]+$/;
const DRAFT_OPEN = /^wizard\/(?!new(?:$|-|\?))[^/?]+$/;
/** Строка переписки «Чатов» — `thread?dialog=<id>` (без `around`/`inside`). */
const THREAD_OPEN = /^thread\?dialog=[^&]+$/;

export const WARM_PLAN: Readonly<Record<string, readonly WarmRule[]>> = {
  // ── Служебные ключи реестра — не экраны (конец §4) ──
  sample: [never('образец схемы')],
  'stub/bots': [never('переходная страница')],
  'stub/chats': [never('переходная страница')],
  'stub/account': [never('переходная страница')],

  // ── §4.1 Пачка при запуске ──
  bots: [
    launch('1', 'вкладка «Боты»', { kind: 'fixed', key: 'bots' }),
    launch('2', 'пилюля папки «Ботов»', { kind: 'folders', root: 'bots' }),
  ],
  // QUICK-START FIX-2 §6.4, FIX-3 §7.2–7.5: корень «+» — лёгкий, в пачке запуска; «Быстрая настройка» и «Каталог ботов»
  // (со сценами) — касанием своей строки; «Инструкции» — касанием ссылки, их статьи — касанием строки (правило шаблона
  // `help/<раздел>/<статья>` ниже).
  'bots/templates': [launch('3', '«+» шапки «Ботов»', { kind: 'fixed', key: 'bots/templates' })],
  'bots/templates/catalog': [touch('24', 'bots/templates (строка «Каталог ботов»)')],
  'bots/templates/guides': [touch('24', 'bots/templates (ссылка «Инструкции — пошаговые руководства»)')],
  'help/quick-start': [touch('24', 'bots/templates (строка «Быстрая настройка»)')],
  'bots/folders': [
    launch('4', 'шестерёнка полосы папок «Ботов»', { kind: 'fixed', key: 'bots/folders' }),
    touch('61', 'account/settings'),
  ],
  '^bot\\/([^/]+)$': [
    launch('5', 'карточка бота (видимые)', { kind: 'rows', root: 'bots', re: BOT_WINDOW_OPEN }),
    touch('29', 'bot/<id> (переключатель периода)'),
  ],
  '^wizard\\/([^/]+)$': [
    launch('6', 'карточка черновика (видимые)', { kind: 'rows', root: 'bots', re: DRAFT_OPEN }),
    touch('24', 'bots/templates/catalog (сборкам ниже QUICK_START_MIN_BUILD — bots/templates)'),
    touch('32', 'wizard/<id> (шаг)'),
  ],
  chats: [
    launch('7', 'вкладка «Чаты»', { kind: 'fixed', key: 'chats' }),
    launch('8', 'пилюля папки «Чатов»', { kind: 'folders', root: 'chats' }),
  ],
  broadcasts: [launch('9', 'самолётик шапки «Чатов»', { kind: 'fixed', key: 'broadcasts' })],
  'chats/folders': [
    launch('10', 'шестерёнка полосы папок «Чатов»', { kind: 'fixed', key: 'chats/folders' }),
    touch('61', 'account/settings'),
  ],
  thread: [
    launch('11', 'строка переписки (видимые)', { kind: 'rows', root: 'chats', re: THREAD_OPEN, add: 'limit=20' }),
    touch('31', 'bot/<id>'),
    queue('42', 'thread (открыта — ранние сообщения, З-7)'),
    queue('43', 'thread (всё скачано — второй, третий экран, З-7)'),
    touch('45', 'chats (строка поиска)'),
    touch('70', 'contact/<id>'),
  ],
  account: [launch('12', 'вкладка «Аккаунт»', { kind: 'fixed', key: 'account' })],
  inbox: [launch('13', 'колокольчик шапки «Аккаунта»', { kind: 'fixed', key: 'inbox' })],
  'account/profile': [launch('14', 'герой «Аккаунта»', { kind: 'fixed', key: 'account/profile' })],
  'account/plan': [launch('15', 'строка «Аккаунта»', { kind: 'fixed', key: 'account/plan' })],
  'account/notifications': [launch('16', 'строка «Аккаунта»', { kind: 'fixed', key: 'account/notifications' })],
  'account/security': [launch('17', 'строка «Аккаунта»', { kind: 'fixed', key: 'account/security' })],
  'account/lang': [launch('18', 'строка «Аккаунта»', { kind: 'fixed', key: 'account/lang' })],
  'account/settings': [launch('19', 'строка «Аккаунта»', { kind: 'fixed', key: 'account/settings' })],
  help: [launch('20', 'строка «Аккаунта»', { kind: 'fixed', key: 'help' })],

  // ── §4.2 Боты ──
  '^bots\\/folders\\/([^/]+)$': [touch('21', 'bots/folders')],
  'bots/folders/new': [touch('22', 'bots/folders')],
  'bots/scenes': [queue('23', 'запуск (сцены мастера, служебная)')],
  '^bot\\/([^/]+)\\/about$': [touch('25', 'bot/<id> (меню карточки / шапка окна)')],
  '^bot\\/([^/]+)\\/dup$': [touch('26', 'bot/<id> (меню)')],
  '^bot\\/([^/]+)\\/folder$': [touch('27', 'bot/<id> (меню / окно)')],
  '^bot\\/([^/]+)\\/stats$': [touch('28', 'bot/<id> (меню окна / «Подключение»)')],
  '^contact\\/([^/]+)$': [touch('30', 'bot/<id>'), touch('44', 'thread («i» шапки)'), touch('69', 'contacts')],

  // ── §4.2 Мастер (все — О от шага) ──
  '^wizard\\/([^/]+)\\/preview$': [touch('33', 'wizard/<id> (шаг)')],
  // QUICK-START §11.8: шаг поверх итога «Всё готово» — касанием строки итога.
  '^wizard\\/([^/]+)\\/over\\/([^/]+)$': [touch('34', 'wizard/<id> (итог «Всё готово»)')],
  '^wizard\\/([^/]+)\\/item\\/([^/]+)\\/([^/]+)$': [touch('34', 'wizard/<id> (шаг)')],
  '^wizard\\/([^/]+)\\/item\\/([^/]+)\\/(\\d+)\\/rows$': [touch('34', 'wizard/<id> (шаг)')],
  '^wizard\\/([^/]+)\\/item\\/([^/]+)\\/(\\d+)\\/row\\/([^/]+)$': [touch('34', 'wizard/<id>/item/<поле>/<n>/rows')],
  '^wizard\\/([^/]+)\\/item\\/([^/]+)\\/(\\d+)\\/when\\/([^/]+)(?:\\/([^/]+))?$': [touch('34', 'wizard/<id>/item/<поле>/<n>/rows')],
  '^wizard\\/([^/]+)\\/lines\\/([^/]+)$': [touch('35', 'wizard/<id> (шаг)')],
  '^wizard\\/([^/]+)\\/posts$': [touch('36', 'wizard/<id> (шаг)')],
  '^wizard\\/([^/]+)\\/chan$': [touch('37', 'wizard/<id> (шаг)')],
  '^wizard\\/([^/]+)\\/conn$': [touch('37', 'wizard/<id> (шаг)')],
  '^message\\/([^/]+)\\/([^/]+)$': [touch('38', 'wizard/<id> (шаг)')],
  '^message\\/([^/]+)\\/([^/]+)\\/button\\/([^/]+)$': [touch('38', 'message/<id>/<параметр>')],
  // QUICK-START §11.35 ч.2: «Каталог сообщений» окна письма цепочки — касанием окна; группа — касанием каталога.
  '^message\\/([^/]+)\\/([^/]+)\\/catalog$': [touch('38', 'message/<id>/<параметр>')],
  '^message\\/([^/]+)\\/([^/]+)\\/catalog\\/([^/]+)$': [touch('38', 'message/<id>/<параметр>/catalog')],

  // ── §4.2 Чаты и переписка ──
  'chats/filters': [touch('39', 'chats (шапка)')],
  '^chats\\/folders\\/([^/]+)$': [touch('40', 'chats/folders')],
  'chats/folders/new': [touch('40', 'chats/folders')],
  '^chats\\/folders\\/([^/]+)\\/pick\\/([a-z]+)$': [touch('41', 'chats/folders/<id>')],

  // ── §4.2 Рассылки ──
  '^broadcast\\/([^/]+)$': [touch('46a', 'broadcasts («+»)'), touch('46b', 'broadcasts (строка)')],
  '^broadcast\\/([^/]+)\\/stats$': [touch('46c', 'broadcasts (строка / меню)')],
  '^broadcast\\/([^/]+)\\/run\\/([^/]+)$': [touch('46d', 'broadcast/<id>/stats')],
  '^broadcast\\/([^/]+)\\/audience$': [touch('46e', 'broadcast/<id>')],
  '^broadcast\\/([^/]+)\\/audience\\/filters$': [touch('46e', 'broadcast/<id>/audience')],
  '^broadcast\\/([^/]+)\\/audience\\/filters\\/([^/]+)$': [touch('46e', 'broadcast/<id>/audience/filters')],

  // ── §4.2 Аккаунт и настройки ──
  'account/profile/name': [touch('47', 'account/profile')],
  'account/delete': [touch('48', 'account/profile')],
  'account/notifications/marketing': [touch('49', 'account/notifications')],
  'account/notifications/messages': [touch('49', 'account/notifications')],
  'account/notifications/quiet': [touch('49', 'account/notifications')],
  'account/notifications/urgent': [touch('49', 'account/notifications')],
  'account/tz': [touch('50', 'account/lang')],
  'account/theme': [touch('50', 'account/lang')],
  'account/locale': [queue('51', 'пуш / ссылка (не экран)')],
  'account/cabinets': [touch('52', 'account / account/settings (только у сотрудника)')],
  'account/earnings': [queue('53', 'ссылка веба')],
  'account/diagnostics': [{ when: 'never', from: 'входа с экрана нет (account.ts)', row: '54' }],
  'account/stats': [touch('55', 'connections/<id> («Подключение»)')],
  connections: [touch('56', 'account/settings')],
  contacts: [touch('57', 'account/settings'), touch('58', 'contacts (пилюля папки)')],
  'contacts/folders': [touch('59', 'account/settings / шестерёнка «Контактов»')],
  'contacts/tags': [touch('60', 'account/settings')],

  // ── §4.2 Подключения ──
  'connections/add': [touch('62', 'connections')],
  'connections/add/telegramApi': [touch('63', 'connections/add')],
  'connections/add/telegramApi/how': [touch('64', 'connections/add/telegramApi')],
  'connections/add/facebookGraphApi': [touch('65', 'connections/add')],
  '^connections\\/(?!add$)([^/]+)$': [touch('66', 'connections')],
  '^connections\\/([^/]+)\\/niche$': [
    touch('67', 'connections/<id>'),
    // Шаблон ответа сервера после подключения (`connections/{resp:id}/niche?flow=1`, connections.ts) — не экран.
    never('шаблон ответа после подключения'),
  ],
  '^connections\\/([^/]+)\\/stats$': [touch('68', 'connections/<id>')],

  // ── §4.2 Контакты ──
  '^contact\\/([^/]+)\\/tags$': [touch('71', 'contact/<id>')],
  '^contact\\/([^/]+)\\/history\\/([^/]+)$': [touch('72', 'contact/<id>')],
  '^contact\\/([^/]+)\\/chain\\/([^/]+)$': [touch('73', 'contact/<id>')],
  'contacts/filters': [touch('74', 'contacts (шапка)')],
  '^contacts\\/filters\\/(.+)$': [touch('74', 'contacts/filters')],
  '^contacts\\/folders\\/([^/]+)$': [touch('75', 'contacts/folders')],
  'contacts/folders/new': [touch('75', 'contacts/folders')],
  '^contacts\\/folders\\/([^/]+)\\/f\\/(.+)$': [touch('76', 'contacts/folders/<id>')],
  '^contacts\\/tags\\/(?!new$)(.+)$': [touch('77', 'contacts/tags')],
  'contacts/tags/new': [touch('77', 'contacts/tags')],

  // ── §4.2 Справка ──
  '^help\\/([a-z0-9-]+)$': [touch('78', 'help')],
  '^help\\/([a-z0-9-]+)\\/([a-z0-9-]+)$': [touch('79', 'help/<раздел>'), touch('24', 'bots/templates/guides (строка статьи)')],
  '^help\\/legal\\/(privacy|terms)$': [touch('80', 'help')],
};

/** Правила пачки запуска (`when === 'launch'`) в порядке таблицы §4.1. */
export function launchRules(): WarmRule[] {
  return Object.values(WARM_PLAN)
    .flat()
    .filter((r) => r.when === 'launch')
    .sort((a, b) => Number(a.row) - Number(b.row));
}

// ═══════════════════════════════════════════ 3. АДМИНСКОЕ iOS (Scriptera A) ═══════════════════════════════════════════

/**
 * РАЗДЕЛ АДМИНКИ — тот же формат (страница → цели с уровнем), исполнитель — `AdmWarm` приложения Scriptera A (репо
 * scriptera-adm, `ios/App/App/Adm/AdmWarm.swift`). До 26.10.2026 приложение решало само: `AdmChats.warmNeighbors`
 * (первые страницы прочих папок, раз в минуту, только без кэша), `AdmLead.warm` (палец на ряду «Продаж»/«Площадок»).
 * Теперь места только сообщают исполнителю «страница показана» (ключ + ответ сервера) и «палец на строке», а ЧТО греть,
 * на каком уровне и в каком порядке — отсюда; числа — `WARM_QUEUES.adm`.
 *
 * Ключ записи — страница сервера админки: путь маршрута под `/api/admin/app/` (`lead/[id]` — динамический сегмент),
 * плюс `@launch` — вход в кабинет (У1). Цель — `ref`:
 *   • путь маршрута (`thread`, `lead/[id]`, …) — ключи берутся из показанной страницы: все `open` её ответа (ряды,
 *     кнопки, «i» шапки), подходящие под этот маршрут, сверху вниз; `first` — сколько первых (нет — все);
 *   • `@folders` — первые страницы прочих папок этого же списка (ключи даёт список: та же папка без фильтра и поиска);
 *   • `@roots` — корни вкладок-списков, которых сейчас нет на экране (ключи даёт приложение: сохранённая папка).
 * `touch: true` — цель только по пальцу на строке (`ref` — маршрут ключа строки), сразу и мимо очереди (`WARM_TOUCH`).
 * Страница с пустым списком — с неё ничего не греется (действие, выбор, лист без переходов).
 * Ключ прогретого = ключ экрана (GET `/api/admin/app/<ключ>&tz=` → кэш `AdmCache` под `<ключ>`): экран, открытый
 * позже, берёт прогретое тем же кадром. Экраны денег и статусов (`SERVER_TRUTH`) у админки нет — сохранённое
 * показывается всегда (кэш `AdmCache` без порога возраста).
 *
 * INVARIANT(scriptera): каждая GET-страница `app/api/admin/app/**` есть здесь (замок warm-plan.site.test.ts), и `ref`
 * называют только страницы этого раздела или `@folders`/`@roots`. Причина: «что и когда греть — только файл-план»
 * (владелец 26.10.2026). Канон: ТЗ HELP-WARM-FIX §2 п.3.
 */
export interface AdmWarmRule {
  /** Цель (имя как у сайта: `adm.lead`, `adm.thread`, …) — для журнала и сверки с браузерной админкой. */
  id: string;
  level: WarmLevel;
  what: string;
  /** Маршрут цели под `/api/admin/app/` или `@folders` / `@roots`. */
  ref: string;
  /** Только первые N ключей страницы (сверху вниз). Нет — все. */
  first?: number;
  /** Цель — по пальцу на строке, не по показу страницы. */
  touch?: boolean;
}

/** Первых строк списка — столько же, сколько у родных списков клиента и браузерной админки (`NATIVE_WARM_FIRST`). */
const ADM_FIRST_ROWS = 12;

const adm = (id: string, level: WarmLevel, ref: string, what: string, extra: Partial<AdmWarmRule> = {}): AdmWarmRule => ({
  id,
  level,
  ref,
  what,
  ...extra,
});

/** Все GET-страницы сервера админки (`app/api/admin/app/**`) + вход. */
export const ADM_WARM_PLAN: Readonly<Record<string, readonly AdmWarmRule[]>> = {
  // ── Вход в кабинет (У1): «Чаты» грузит сам экран, «Аккаунт» — сам при постановке; невидимые списки — сюда. ──
  '@launch': [adm('adm.roots', 1, '@roots', 'корни вкладок «Продажи» и «Площадки» (сохранённая папка), пока их нет на экране')],

  // ── Списки вкладок ──
  chats: [
    adm('adm.thread', 2, 'thread', 'переписки первых строк', { first: ADM_FIRST_ROWS }),
    adm('adm.folders', 2, '@folders', 'первые страницы прочих папок «Чатов» (свайп)'),
  ],
  sales: [
    adm('adm.lead', 2, 'lead/[id]', 'карточки клиентов первых строк', { first: ADM_FIRST_ROWS }),
    adm('adm.lead', 2, 'lead/[id]', 'карточка клиента — палец на строке', { touch: true }),
    adm('adm.folders', 2, '@folders', 'первые страницы прочих папок «Продаж» (свайп)'),
  ],
  platforms: [
    adm('adm.platform', 2, 'platform/[id]', 'карточки площадок первых строк', { first: ADM_FIRST_ROWS }),
    adm('adm.platform', 2, 'platform/[id]', 'карточка площадки — палец на строке', { touch: true }),
    adm('adm.folders', 2, '@folders', 'первые страницы прочих папок «Площадок» (свайп)'),
  ],

  // ── Переписка: «i» шапки ведёт в карточку ──
  thread: [
    adm('adm.lead', 2, 'lead/[id]', 'карточка клиента («i» шапки)'),
    adm('adm.guest', 2, 'guest/[tg]', 'профиль гостя («i» шапки)'),
  ],

  // ── Карточки (стопка): строки «Открыть чат», «Боты клиента», «Что впереди», «Условия» ──
  'lead/[id]': [
    adm('adm.thread', 2, 'thread', 'переписка клиента («Открыть чат»)'),
    adm('adm.lead-bots', 2, 'lead/[id]/bots', '«Боты клиента»'),
    adm('adm.ahead', 2, 'lead/[id]/ahead/[once]', 'шторки «Что уйдёт» строк «Что впереди»'),
    adm('adm.terms', 3, 'lead/[id]/terms', '«Условия клиента»'),
  ],
  'guest/[tg]': [adm('adm.thread', 2, 'thread', 'переписка гостя')],
  'platform/[id]': [
    adm('adm.thread', 2, 'thread', 'переписка владельца площадки'),
    adm('adm.lead-bots', 2, 'lead/[id]/bots', '«Боты клиента»'),
    adm('adm.ahead', 2, 'lead/[id]/ahead/[once]', 'шторки «Что уйдёт»'),
  ],
  'lead/[id]/bots': [adm('adm.lead-bot', 3, 'lead/[id]/bot/[draftId]', 'страницы ботов клиента', { first: ADM_FIRST_ROWS })],

  // ── Листы без переходов, выбор и действия — с них не греется ──
  'lead/[id]/bot/[draftId]': [],
  'lead/[id]/bot/[draftId]/letter/[n]': [],
  'lead/[id]/ahead/[once]': [],
  'lead/[id]/terms': [],
  'lead/[id]/channel': [],
  'lead/[id]/merge-pick': [],
  'platform/[id]/kinds': [],
  event: [],
  account: [],
  folders: [],
  'folder/[id]': [],
  'folder-pick': [],
  filters: [],
  sample: [],
};
export const ADM_WARM_NOTE =
  'Исполнитель — AdmWarm (Scriptera A): страница показана → цели её записи по уровням (У1 вход, У2 после первого экрана, У3 в простой), палец на строке — сразу; ключи — `open` ответа страницы, `@folders`/`@roots` даёт список.';

// ═══════════════════════════════════════════ ОТДАЧА ПЛАНА ПРИЛОЖЕНИЯМ ═══════════════════════════════════════════════

/** Правило страницы приложения в ответе ручки (RegExp пачки не сериализуется — у приложения своя пачка, `launch`). */
export interface WarmPlanAppRule {
  when: WarmWhen;
  level: WarmLevel | null;
  from: string;
  row: string;
}

/**
 * ТЕЛО `GET /api/app/warm-plan` — план для приложений (сайт читает файл напрямую). Форма — только добавления
 * (`format` растёт), поля ниже не переименовываются и не удаляются (CLAUDE.md запрет №10).
 */
export function warmPlanPayload(): {
  ok: true;
  format: number;
  queue: typeof WARM_QUEUE;
  queues: typeof WARM_QUEUES;
  levels: Readonly<Record<WarmLevel, string>>;
  touch: string;
  serverTruth: readonly ServerTruthEntry[];
  app: { common: readonly AppWarmCommon[]; pages: Record<string, WarmPlanAppRule[]> };
  adm: { pages: Readonly<Record<string, readonly AdmWarmRule[]>>; note: string };
} {
  const pages: Record<string, WarmPlanAppRule[]> = {};
  for (const [key, rules] of Object.entries(WARM_PLAN)) {
    pages[key] = rules.map((r) => ({ when: r.when, level: appRuleLevel(r.when), from: r.from, row: r.row }));
  }
  return {
    ok: true,
    format: WARM_PLAN_FORMAT,
    queue: WARM_QUEUE,
    queues: WARM_QUEUES,
    levels: WARM_LEVELS,
    touch: WARM_TOUCH,
    serverTruth: SERVER_TRUTH,
    app: { common: APP_WARM_COMMON, pages },
    adm: { pages: ADM_WARM_PLAN, note: ADM_WARM_NOTE },
  };
}
